import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { resolveRichText, stripEdgeEmptyParagraphs, verifyCrossLinks } from '@vibesites/runtime';
import { EditableToken, RichTextContent, useContentToken, useVibesites } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * Schedule A Tour ships with `visible: false` (kept out of the nav), so the
 * runtime's cross-link check would unwrap blurb links to it into plain text —
 * even though the page is live and linked from the header and CTAs.
 */
const ALWAYS_LINKABLE_PATHS = ['/schedule-a-tour'];

const BLANK_P_RE = /<p\b[^>]*>(?:\s|<br\s*\/?>)*<\/p>/gi;
const BLOCK_CONTENT_RE = /<(?:p|div|h[1-6]|ul|ol|li|blockquote)[\s>]/i;

/** Single-paragraph blurbs render their inner HTML directly inside the outer <p>. */
function unwrapSingleParagraph(html: string): string {
  const cleaned = stripEdgeEmptyParagraphs(html);
  const match = cleaned.match(/^<p>([\s\S]*)<\/p>$/);
  if (!match) return cleaned || html;
  if (/<p[\s>]|<div[\s>]|<h[1-6][\s>]/i.test(match[1])) return cleaned;
  return match[1];
}

function BlurbRichText({
  path,
  defaultField,
  defaultValue,
  className,
}: {
  path: string;
  defaultField?: string;
  defaultValue: string;
  className: string;
}) {
  const { property, pagePaths } = useVibesites();
  const rawHtml = useContentToken(path, { defaultField, defaultValue });
  const resolved = rawHtml ? resolveRichText(rawHtml, property as Record<string, any>) : '';
  const linkablePaths = pagePaths?.length ? [...pagePaths, ...ALWAYS_LINKABLE_PATHS] : pagePaths;
  const html = unwrapSingleParagraph(verifyCrossLinks(resolved, linkablePaths)).replace(BLANK_P_RE, '');
  const Tag = BLOCK_CONTENT_RE.test(html) ? 'div' : 'p';

  return (
    <EditableToken
      path={path}
      type="richtext"
      defaultField={defaultField}
      defaultValue={defaultValue}
      as={Tag}
      className={className}
    >
      <span data-vibesite-richtext="" dangerouslySetInnerHTML={{ __html: html }} style={{ display: 'contents' }} />
    </EditableToken>
  );
}

interface PageBlurbProps {
  /** Property field name for the blurb (e.g. 'property_floorplans_blurb') */
  blurbField?: string;
  /** Unique prefix so each page's blurb token is independently editable */
  tokenPrefix?: string;
  /** Static fallback text when no property blurb is available */
  defaultText?: string;
}

export function PageBlurb({ blurbField, tokenPrefix, defaultText }: PageBlurbProps) {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const prefix = tokenPrefix || 'pageblurb';
  const textPath = `${prefix}.text`;

  return (
    <section ref={sectionRef as React.RefObject<HTMLElement>} className="reveal-fade-up py-10 md:py-14 bg-[#F5F6F7]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <BlurbRichText
          path={textPath}
          defaultField={blurbField}
          defaultValue={defaultText || ''}
          className="font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-muted)]"
        />
      </div>

      {/* Schema-lint anchor for static analysis — hidden at runtime */}
      <RichTextContent
        path="pageblurb.text"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PageBlurb',
  displayName: 'Page Blurb',
  tokens: {
    'pageblurb.text': {
      label: 'Blurb Text',
      type: 'richtext',
      defaultValue: '<span data-type="field-token" data-field="property_long_description"></span>',
      suggestedFields: [
        'property_floorplans_blurb',
        'property_amenities_blurb',
        'property_gallery_blurb',
        'property_neighborhood_blurb',
        'property_virtualtours_blurb',
        'property_contactus_blurb',
        'property_reviews_blurb',
        'property_pets_blurb',
        'property_applynow_blurb',
        'property_residentaccess_blurb',
        'property_scheduletour_blurb',
        'property_interactivemap_blurb',
      ],
    },
  },
};

(PageBlurb as any).contentSchema = contentSchema;

export default PageBlurb;
